import { AxiosError, AxiosResponse, Canceler } from 'axios';
import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from 'react-query';
import {
  fetchLanes,
  fetchLane,
  createLane,
  updateLane,
  updateLaneCollections,
  deleteLane,
  duplicateLane,
  fetchFlows,
  fetchFlow,
  createFlow,
  updateFlow,
  deleteFlow,
  duplicateFlow,
  updateFlowMappers,
} from '../../services/api/Onboarding';
import {
  IPaginationResponseDto,
  ISuccessResult,
  IOnboardingLane,
  IOnboardingFlow,
  IOnboardingCollectionMapper,
  IOnboardingFlowLaneMapper,
} from '../../services/types';
import useBackdrop from '../useBackdrop';
import useSnackbar from '../useSnackbar';

enum QueryKey {
  AllLanes = 'AllLanes',
  Lane = 'Lane',
  AllFlows = 'AllFlows',
  Flow = 'Flow',
}

interface IUseLaneMutation {
  create: UseMutationResult<IOnboardingLane, Error, Partial<IOnboardingLane>>;
  update: UseMutationResult<IOnboardingLane, Error, Partial<IOnboardingLane>>;
  remove: UseMutationResult<ISuccessResult<IOnboardingLane>, Error, Partial<IOnboardingLane>>;
  duplicate: UseMutationResult<ISuccessResult<IOnboardingLane>, Error, Partial<IOnboardingLane>>;
}
interface IUseFlowMutation {
  create: UseMutationResult<IOnboardingFlow, Error, Partial<IOnboardingFlow>>;
  update: UseMutationResult<IOnboardingFlow, Error, Partial<IOnboardingFlow>>;
  remove: UseMutationResult<ISuccessResult<IOnboardingFlow>, Error, Partial<IOnboardingFlow>>;
  duplicate: UseMutationResult<ISuccessResult<IOnboardingFlow>, Error, Partial<IOnboardingFlow>>;
}

export const useLanes = (page = 1, limit = 20): UseQueryResult<IPaginationResponseDto<IOnboardingLane>, AxiosError> =>
  useQuery<IPaginationResponseDto<IOnboardingLane>, AxiosError>(
    [QueryKey.AllLanes, { page, limit }],
    async () => {
      const [fetchPromise] = fetchLanes(page, limit);
      const result = await fetchPromise;

      if (result.status !== 200) {
        throw new Error('API Error');
      }

      return result.data;
    },
    {
      onError: e => {
        console.log(e.response ? e.response.data.message : e.message);
      },
      keepPreviousData: true,
    },
  );

export const useLane = (laneId?: number): UseQueryResult<IOnboardingLane | undefined, AxiosError> =>
  useQuery<IOnboardingLane | undefined, AxiosError>(
    [QueryKey.Lane, laneId],
    async () => {
      if (!laneId) {
        return;
      }
      const [fetchPromise] = fetchLane(laneId);
      const result = await fetchPromise;

      if (result.status !== 200) {
        throw new Error('API Error');
      }

      return result.data;
    },
    {
      onError: e => {
        console.log(e.response ? e.response.data.message : e.message);
      },
      keepPreviousData: true,
    },
  );

export const useFlows = (page = 1, limit = 20): UseQueryResult<IPaginationResponseDto<IOnboardingFlow>, AxiosError> =>
  useQuery<IPaginationResponseDto<IOnboardingFlow>, AxiosError>(
    [QueryKey.AllFlows, { page, limit }],
    async () => {
      const [fetchPromise] = fetchFlows(page, limit);
      const result = await fetchPromise;

      if (result.status !== 200) {
        throw new Error('API Error');
      }

      return result.data;
    },
    {
      onError: e => {
        console.log(e.response ? e.response.data.message : e.message);
      },
      keepPreviousData: true,
    },
  );

export const useFlow = (flowId?: number): UseQueryResult<IOnboardingFlow | undefined, AxiosError> =>
  useQuery<IOnboardingFlow | undefined, AxiosError>(
    [QueryKey.Flow, flowId],
    async () => {
      if (!flowId) {
        return;
      }
      const [fetchPromise] = fetchFlow(flowId);
      const result = await fetchPromise;

      if (result.status !== 200) {
        throw new Error('API Error');
      }

      return result.data;
    },
    {
      onError: e => {
        console.log(e.response ? e.response.data.message : e.message);
      },
      keepPreviousData: true,
    },
  );

export const useLaneMutation = (): IUseLaneMutation => {
  const snackbar = useSnackbar();
  const backdrop = useBackdrop();
  const queryClient = useQueryClient();

  const remove = useMutation<ISuccessResult<IOnboardingLane>, AxiosError, Partial<IOnboardingLane>>(
    async (lane: Partial<IOnboardingLane>) => {
      if (!lane.id) {
        throw new Error('id is necessary to remove');
      }

      backdrop.show();

      const { data: apiResult } = await deleteLane(lane.id);
      if (!apiResult.success) {
        throw new Error(apiResult.message);
      }

      return apiResult;
    },
    {
      onSuccess: (data, variables) => {
        snackbar.success('Deleted successfully.');
        queryClient.removeQueries([QueryKey.Lane, variables.id]);
        queryClient.invalidateQueries(QueryKey.AllLanes);
      },
      onError: e => {
        snackbar.error(e);
      },
      onSettled: () => {
        backdrop.hide();
      },
    },
  );

  const duplicate = useMutation<ISuccessResult<IOnboardingLane>, AxiosError, Partial<IOnboardingLane>>(
    async (lane: Partial<IOnboardingLane>) => {
      if (!lane.id) {
        throw new Error('id is necessary to remove');
      }

      backdrop.show();

      const { data: apiResult } = await duplicateLane(lane.id);
      if (!apiResult.success) {
        throw new Error(apiResult.message);
      }

      return apiResult;
    },
    {
      onSuccess: (data, variables) => {
        snackbar.success('Deplicated successfully.');
        queryClient.invalidateQueries(QueryKey.AllLanes);
      },
      onError: e => {
        snackbar.error(e);
      },
      onSettled: () => {
        backdrop.hide();
      },
    },
  );

  const create = useMutation<IOnboardingLane, AxiosError, Partial<IOnboardingLane>>(
    async (lane: Partial<IOnboardingLane>) => {
      backdrop.show();
      const { data: apiResult } = await createLane(lane);

      if (!apiResult.success) {
        throw new Error(apiResult.message);
      }

      return apiResult.data;
    },
    {
      onSuccess: () => {
        snackbar.success('Created successfully.');
        queryClient.invalidateQueries(QueryKey.AllLanes);
      },
      onError: e => {
        snackbar.error(e);
      },
      onSettled: () => {
        backdrop.hide();
      },
    },
  );

  const update = useMutation<IOnboardingLane, AxiosError, Partial<IOnboardingLane>>(
    async (lane: Partial<IOnboardingLane>) => {
      if (!lane.id) {
        throw new Error('id is necessary to update');
      }

      backdrop.show();
      // update lane
      const { data: apiResult } = await updateLane(lane.id, lane);
      if (!apiResult.success) {
        throw new Error(apiResult.message);
      }

      return apiResult.data;
    },
    {
      onSuccess: (data, variables) => {
        snackbar.success('Updated successfully.');
        // update 직후 database replication sync 문제가 있어서 delay를 둠
        setTimeout(() => {
          queryClient.invalidateQueries(QueryKey.AllLanes);
          queryClient.invalidateQueries([QueryKey.Lane, variables.id]);
        }, 300);
      },
      onError: e => {
        snackbar.error(e);
      },
      onSettled: () => {
        backdrop.hide();
      },
    },
  );

  return {
    remove,
    create,
    update,
    duplicate,
  };
};

export const useFlowMutation = (): IUseFlowMutation => {
  const snackbar = useSnackbar();
  const backdrop = useBackdrop();
  const queryClient = useQueryClient();

  const remove = useMutation<ISuccessResult<IOnboardingFlow>, AxiosError, Partial<IOnboardingFlow>>(
    async (flow: Partial<IOnboardingFlow>) => {
      if (!flow.id) {
        throw new Error('id is necessary to remove');
      }

      backdrop.show();

      const { data: apiResult } = await deleteFlow(flow.id);
      if (!apiResult.success) {
        throw new Error(apiResult.message);
      }

      return apiResult;
    },
    {
      onSuccess: (data, variables) => {
        snackbar.success('Deleted successfully.');
        queryClient.removeQueries([QueryKey.Flow, variables.id]);
        queryClient.invalidateQueries(QueryKey.AllFlows);
      },
      onError: e => {
        snackbar.error(e);
      },
      onSettled: () => {
        backdrop.hide();
      },
    },
  );

  const duplicate = useMutation<ISuccessResult<IOnboardingFlow>, AxiosError, Partial<IOnboardingFlow>>(
    async (flow: Partial<IOnboardingFlow>) => {
      if (!flow.id) {
        throw new Error('id is necessary to remove');
      }

      backdrop.show();

      const { data: apiResult } = await duplicateFlow(flow.id);
      if (!apiResult.success) {
        throw new Error(apiResult.message);
      }

      return apiResult;
    },
    {
      onSuccess: (data, variables) => {
        snackbar.success('Deplicated successfully.');
        queryClient.invalidateQueries(QueryKey.AllFlows);
      },
      onError: e => {
        snackbar.error(e);
      },
      onSettled: () => {
        backdrop.hide();
      },
    },
  );

  const create = useMutation<IOnboardingFlow, AxiosError, Partial<IOnboardingFlow>>(
    async (flow: Partial<IOnboardingFlow>) => {
      backdrop.show();
      const { data: apiResult } = await createFlow(flow);

      if (!apiResult.success) {
        throw new Error(apiResult.message);
      }

      return apiResult.data;
    },
    {
      onSuccess: () => {
        snackbar.success('Created successfully.');
        queryClient.invalidateQueries(QueryKey.AllFlows);
      },
      onError: e => {
        snackbar.error(e);
      },
      onSettled: () => {
        backdrop.hide();
      },
    },
  );

  const update = useMutation<IOnboardingFlow, AxiosError, Partial<IOnboardingFlow>>(
    async (flow: Partial<IOnboardingFlow>) => {
      if (!flow.id) {
        throw new Error('id is necessary to update');
      }

      backdrop.show();
      const { data: apiResult } = await updateFlow(flow.id, flow);

      if (!apiResult.success) {
        throw new Error(apiResult.message);
      }

      return apiResult.data;
    },
    {
      onSuccess: (data, variables) => {
        snackbar.success('Updated successfully.');
        // update 직후 database replication sync 문제가 있어서 delay를 둠
        setTimeout(() => {
          queryClient.invalidateQueries(QueryKey.AllFlows);
          queryClient.invalidateQueries([QueryKey.Flow, variables.id]);
        }, 300);
      },
      onError: e => {
        snackbar.error(e);
      },
      onSettled: () => {
        backdrop.hide();
      },
    },
  );

  return {
    remove,
    create,
    update,
    duplicate,
  };
};
